/**
 * Helpers for F5 schedule-task realtime:
 * ChatThread room (joinChat) + taskId filter when present.
 * Classic ai-thinking frames are thread-scoped (no taskId) — map via chatThreadId.
 * HTTP runtime-state only on reconnect / join failure (not a live poll).
 */

export function normalizeGuid(value) {
  if (value == null || value === '') return null;
  return String(value).trim().toUpperCase();
}

/** Chat stream frames may include taskId — their `status` is NOT InternalTaskStatus. */
function isChatStreamMessageType(messageType) {
  const nmt = typeof messageType === 'string' ? messageType.toLowerCase() : '';
  return (
    nmt === 'ai-thinking' ||
    messageType === 9 ||
    nmt === 'response' ||
    messageType === 6 ||
    nmt === 'engine-ai-message' ||
    nmt === 'engineaimessage' ||
    messageType === 13
  );
}

/**
 * Extract schedule-task fields from a socket envelope (design §13).
 * Ignores classic chatMessage frames (ai-thinking / response) even if they
 * carry taskId — those use ChatMessageStatus, not InternalTaskStatus.
 */
export function extractScheduleTaskEnvelope(data) {
  if (!data || typeof data !== 'object') return null;
  if (isChatStreamMessageType(data.messageType)) return null;

  const nested = data.payload && typeof data.payload === 'object' ? data.payload : null;
  const taskId = normalizeGuid(
    data.taskId ||
      nested?.taskId ||
      data.aiAgentScheduleInternalTaskId ||
      nested?.aiAgentScheduleInternalTaskId,
  );
  if (!taskId) return null;

  const chatThreadId = normalizeGuid(
    data.chatThreadId || nested?.chatThreadId || data.threadId || nested?.threadId,
  );

  const activityType = nested?.activityType ?? data.activityType ?? data.latestActivityType;
  const displayText =
    nested?.displayText ||
    nested?.latestActivity ||
    data.latestActivity ||
    data.messagePreview ||
    null;
  const currentToolName = nested?.currentToolName || data.currentToolName || null;
  const status = nested?.status ?? data.status ?? null;
  const requestedOperation = nested?.requestedOperation ?? data.requestedOperation ?? null;
  const sequence = Number(nested?.sequence ?? data.sequence ?? data.lastEventSequence);
  const eventType = data.eventType || nested?.eventType || data.messageType || null;

  return {
    taskId,
    chatThreadId,
    status,
    latestActivity: typeof displayText === 'string' ? displayText : null,
    latestActivityType: activityType ?? null,
    currentToolName: typeof currentToolName === 'string' ? currentToolName : null,
    requestedOperation,
    sequence: Number.isFinite(sequence) ? sequence : null,
    eventType,
    occurredAt: data.occurredAt || nested?.occurredAt || data.created || null,
  };
}

export function mergeRuntimePatch(prev, patch) {
  if (!patch) return prev;
  const next = { ...(prev || {}) };
  if (patch.status != null) next.status = patch.status;
  // Allow explicit null clears (e.g. response settles → drop stale activity).
  if (patch.latestActivity !== undefined) next.latestActivity = patch.latestActivity;
  if (patch.latestActivityType !== undefined) next.latestActivityType = patch.latestActivityType;
  if (patch.currentToolName !== undefined) next.currentToolName = patch.currentToolName;
  if (patch.requestedOperation != null) next.requestedOperation = patch.requestedOperation;
  if (patch.thinking != null) next.thinking = patch.thinking;
  if (patch.thinkingSteps != null) next.thinkingSteps = patch.thinkingSteps;
  if (patch.sequence != null) {
    const prevSeq = Number(next.lastEventSequence ?? next.sequence ?? -1);
    if (patch.sequence >= prevSeq) {
      next.lastEventSequence = patch.sequence;
      next.sequence = patch.sequence;
    } else {
      return next;
    }
  }
  if (patch.chatThreadId) next.chatThreadId = patch.chatThreadId;
  return next;
}
